import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { ChartLineStyle, getLineDashProps, LineThickness, LineStyleProps } from '../lineStyle.types'

const THICKNESS_VALUES: LineThickness[] = [1, 2, 3, 4]
const STYLE_VALUES: ChartLineStyle[] = ['solid', 'dotted', 'dashed', 'large_dashed', 'sparse_dotted']

const STYLE_LABELS: Record<ChartLineStyle, string> = {
    solid: 'Solid',
    dotted: 'Dotted',
    dashed: 'Dashed',
    large_dashed: 'Large dashed',
    sparse_dotted: 'Sparse dotted',
}

const ThicknessSvg = ({ thickness }: { thickness: LineThickness }) => (
    <svg width="24" height="16" style={{ display: 'block' }}>
        <line x1="3" y1="8" x2="21" y2="8" stroke="currentColor" strokeWidth={thickness} strokeLinecap="round" />
    </svg>
)

const StyleSvg = ({ style }: { style: ChartLineStyle }) => {
    const { strokeDasharray, strokeLinecap } = getLineDashProps(style)
    return (
        <svg width="32" height="16" style={{ display: 'block' }}>
            <line
                x1="3" y1="8" x2="29" y2="8"
                stroke="currentColor"
                strokeWidth={2}
                strokeDasharray={strokeDasharray}
                strokeLinecap={strokeLinecap ?? 'butt'}
            />
        </svg>
    )
}

type Props = Required<Pick<LineStyleProps, 'lineThickness' | 'onLineThicknessChange'>> &
    Pick<LineStyleProps, 'lineStyle' | 'onLineStyleChange'>

const SegmentedGroup = <T extends string | number>({
    values,
    selected,
    onSelect,
    renderItem,
    getTitle,
}: {
    values: T[]
    selected: T
    onSelect: (v: T) => void
    renderItem: (v: T) => React.ReactNode
    getTitle: (v: T) => string
}) => (
    <Box sx={{ display: 'flex', width: '100%' }}>
        {values.map((v, i) => {
            const isSelected = v === selected
            return (
                <Button
                    key={String(v)}
                    onClick={() => onSelect(v)}
                    title={getTitle(v)}
                    variant="outlined"
                    size="small"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        px: 0,
                        py: 0.5,
                        borderRadius: i === 0
                            ? '4px 0 0 4px'
                            : i === values.length - 1
                            ? '0 4px 4px 0'
                            : '0',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        backgroundColor: isSelected ? 'action.selected' : 'transparent',
                        position: 'relative',
                        zIndex: isSelected ? 1 : 0,
                        marginLeft: i === 0 ? 0 : '-1px',
                        outline: isSelected ? '1px solid' : 'none',
                        outlineColor: 'primary.main',
                        outlineOffset: '-1px',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'divider',
                            zIndex: 2,
                            outline: '1px solid',
                            outlineColor: isSelected ? 'primary.main' : 'text.disabled',
                            outlineOffset: '-1px',
                        },
                    }}
                >
                    {renderItem(v)}
                </Button>
            )
        })}
    </Box>
)

const LineStylePicker = ({ lineThickness, lineStyle, onLineThicknessChange, onLineStyleChange }: Props) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
            <Typography fontSize="10px" color="text.secondary" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Thickness
            </Typography>
            <SegmentedGroup
                values={THICKNESS_VALUES}
                selected={lineThickness}
                onSelect={onLineThicknessChange}
                renderItem={(t) => <ThicknessSvg thickness={t} />}
                getTitle={(t) => `${t}px`}
            />
        </Box>

        {lineStyle !== undefined && onLineStyleChange && (
            <Box>
                <Typography fontSize="10px" color="text.secondary" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Style
                </Typography>
                <SegmentedGroup
                    values={STYLE_VALUES}
                    selected={lineStyle}
                    onSelect={onLineStyleChange}
                    renderItem={(s) => <StyleSvg style={s} />}
                    getTitle={(s) => STYLE_LABELS[s]}
                />
            </Box>
        )}
    </Box>
)

export { ThicknessSvg, StyleSvg }
export default LineStylePicker
